import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { faArrowLeft, faCheck, faTrash, faUndo, faRedo, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UnitMappingService from '../services/unitMappingService';
import InventoryService from '../services/inventoryService';
import RecipesService from '../services/recipesService';
import RecipeLibraryService from '../services/recipeLibraryService';
import FeaturesService from '../services/FeaturesService';
import http from '../services/httpClient';
import { sortBySearchPriority } from '../utils/searchPriority';
import { autoScaleToSymbol } from '../utils/unitDisplay';
import CustomImageUploadModal from './CustomImageUploadModal';
import PreparationCreatorModal from './PreparationCreatorModal';
import './comps.css';

// Canonical short-symbol -> DB unit_id (matches live GlobalUnits: kg=1, g=2, L=4, ml=5, pcs=9)
const UNIT_SYMBOL_TO_ID = { kg: 1, g: 2, L: 4, ml: 5, pcs: 9 };

const toTitleCase = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';

  return text
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const isPersistableIngredient = (ingredient) => {
  const ingredientName = String(ingredient?.name || ingredient?.ingredient_name || '').trim();
  return ingredientName && ingredientName.toLowerCase() !== 'new ingredient' && Number(ingredient?.quantity) > 0;
};

const formatIngredientQuantity = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '0';
  return Number.isInteger(numericValue) ? String(numericValue) : String(parseFloat(numericValue.toFixed(2)));
};

const SearchableSelect = ({
  value,
  onChange,
  options = [],
  disabled = false,
  style,
  visibleCount = 3,
  isOpen,
  onToggle,
  openUpward,
}) => {
  const [open, setOpen] = useState(isOpen || false);
  const [shouldOpenAbove, setShouldOpenAbove] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 320 });
  const [menuWidth, setMenuWidth] = useState(undefined);
  const wrapRef = useRef(null);
  const controlRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  const handleToggle = (newOpen) => {
    setOpen(newOpen);
    if (onToggle) {
      onToggle(newOpen);
    }
  };

  useEffect(() => {
    if (open && controlRef.current) {
      const VIEWPORT_PADDING = 8;
      const MENU_GAP = 6;
      const MIN_MENU_HEIGHT = 180;
      const FALLBACK_MENU_HEIGHT = 260;

      const updatePosition = () => {
        if (!controlRef.current) return;

        const rect = controlRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const containerEl = controlRef.current.closest('.comps-page-content, .comps-page-container, .comps-toolbar-row');
        const containerRect = containerEl?.getBoundingClientRect();

        const maxContainerWidth = containerRect
          ? Math.max(140, containerRect.width - (VIEWPORT_PADDING * 2))
          : viewportWidth - (VIEWPORT_PADDING * 2);

        const desiredWidth = Math.max(
          rect.width,
          Math.min(menuWidth || rect.width, viewportWidth - (VIEWPORT_PADDING * 2), maxContainerWidth)
        );

        let left = rect.left;
        const minLeft = containerRect
          ? Math.max(VIEWPORT_PADDING, containerRect.left + VIEWPORT_PADDING)
          : VIEWPORT_PADDING;
        const maxLeft = containerRect
          ? Math.min(viewportWidth - VIEWPORT_PADDING - desiredWidth, containerRect.right - VIEWPORT_PADDING - desiredWidth)
          : viewportWidth - VIEWPORT_PADDING - desiredWidth;

        if (left > maxLeft) left = maxLeft;
        if (left < minLeft) left = minLeft;

        const measuredMenuHeight = menuRef.current?.offsetHeight || FALLBACK_MENU_HEIGHT;
        const usableBelow = viewportHeight - rect.bottom - MENU_GAP - VIEWPORT_PADDING;
        const usableAbove = rect.top - MENU_GAP - VIEWPORT_PADDING;

        const preferAbove = !!openUpward;
        const openAbove = preferAbove
          ? usableAbove > 120
          : (usableBelow < MIN_MENU_HEIGHT && usableAbove > usableBelow);

        const availableHeight = Math.max(140, openAbove ? usableAbove : usableBelow);
        const maxHeight = Math.min(measuredMenuHeight, availableHeight);

        const top = openAbove
          ? Math.max(VIEWPORT_PADDING, rect.top - MENU_GAP - maxHeight)
          : Math.min(viewportHeight - VIEWPORT_PADDING - maxHeight, rect.bottom + MENU_GAP);

        setShouldOpenAbove(openAbove);
        setDropdownPosition({ top, left, width: desiredWidth, maxHeight });
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [open, menuWidth, options.length, openUpward]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target) && (!menuRef.current || !menuRef.current.contains(e.target))) {
        handleToggle(false);
      }
    };

    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selected = options.find((option) => option.value === value);
  const orderedOptions = sortBySearchPriority(
    options,
    '',
    (option) => option.label || option.value || '',
    { sortWhenEmpty: true }
  );

  useEffect(() => {
    if (!open) return;

    const ctrl = controlRef.current;
    if (!ctrl) return;

    const computeWidth = () => {
      const testEl = document.createElement('div');
      const computedStyle = window.getComputedStyle(ctrl);
      testEl.style.position = 'fixed';
      testEl.style.left = '-9999px';
      testEl.style.top = '-9999px';
      testEl.style.visibility = 'hidden';
      testEl.style.whiteSpace = 'nowrap';
      testEl.style.fontFamily = computedStyle.fontFamily || 'Inter, Arial, sans-serif';
      testEl.style.fontSize = '16px';
      testEl.style.fontWeight = '400';
      document.body.appendChild(testEl);

      let max = 0;
      const source = orderedOptions.length ? orderedOptions : options;
      for (const option of source) {
        testEl.textContent = String(option.label ?? option.value ?? '');
        max = Math.max(max, testEl.clientWidth);
      }

      document.body.removeChild(testEl);

      const triggerWidth = ctrl.getBoundingClientRect().width;
      let needed = max + 72;
      needed = Math.max(needed, triggerWidth, 96);
      const maxViewport = Math.max(340, Math.floor(window.innerWidth * 0.9));
      setMenuWidth(Math.min(needed, maxViewport));
    };

    computeWidth();
    window.addEventListener('resize', computeWidth);
    return () => window.removeEventListener('resize', computeWidth);
  }, [open, options, orderedOptions]);

  const controlHeight = 30;
  const optionHeight = 36;
  const visibleOptionCount = Math.max(visibleCount, orderedOptions.length, 1);
  const listMaxHeight = visibleOptionCount * optionHeight;
  const computedListMaxHeight = Math.max(
    optionHeight,
    Math.min(listMaxHeight, (dropdownPosition.maxHeight || 320) - 16)
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', ...(style || {}) }}>
      <div
        ref={controlRef}
        onClick={() => !disabled && handleToggle(!open)}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%',
          height: controlHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #d1d5db',
          borderRadius: 8,
          background: disabled ? '#f5f5f5' : '#fff',
          padding: '0 10px',
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label ?? selected?.value ?? 'g'}
        </span>
        <span style={{ border: 'solid #111', borderWidth: '0 2px 2px 0', display: 'inline-block', padding: 3, transform: 'rotate(45deg)' }} />
      </div>
      {open && !disabled && ReactDOM.createPortal(
        <div
          ref={menuRef}
          role="listbox"
          className="comps-unit-menu"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 99999,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            padding: 8,
            width: menuWidth || dropdownPosition.width || 'max-content',
            minWidth: menuWidth || dropdownPosition.width || 'auto',
            maxWidth: '90vw',
            maxHeight: dropdownPosition.maxHeight || '80vh',
            overflow: 'hidden'
          }}
        >
          <div style={{ maxHeight: computedListMaxHeight, overflowY: 'auto', overflowX: 'hidden' }}>
            {orderedOptions.length === 0 ? (
              <div style={{ padding: 12, color: '#777', fontSize: 13 }}>No matches</div>
            ) : (
              orderedOptions.map((option) => (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  onClick={() => {
                    onChange && onChange(option.value);
                    handleToggle(false);
                  }}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: option.value === value ? '#f3f4f6' : 'transparent',
                    color: '#111',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                    wordBreak: 'keep-all'
                  }}
                >
                  {option.label ?? option.value}
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const presetPlateSteps = [1, 10, 100];

const ComplimentaryItemsPage = ({ item, onBack, onSave }) => {
  const [ingredients, setIngredients] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({ name: '', quantity: 1, unit: 'g' });
  const [plateStep, setPlateStep] = useState(1);
  const [showPlateMenu, setShowPlateMenu] = useState(false);
  const [showCustomPlateInput, setShowCustomPlateInput] = useState(false);
  const [customPlateStep, setCustomPlateStep] = useState('');
  const [loading, setLoading] = useState(true);
  const [menuItemId, setMenuItemId] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [availableRecipes, setAvailableRecipes] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [menuSuggestions, setMenuSuggestions] = useState([]);
  const [librarySuggestions, setLibrarySuggestions] = useState([]);
  const [recipeLibraryList, setRecipeLibraryList] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const plateMenuRef = useRef(null);
  const ingredientEditorRef = useRef(null);
  const editNameInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  
  // Auto-save refs and status (INTERNAL ONLY - not DB)
  const autoSaveTimerRef = useRef(null);
  const ingredientsInitRef = useRef(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null);
  
  // Store original ingredients for revert on cancel
  const originalIngredientsRef = useRef([]);
  
  // Undo/Redo history for ingredient changes
  const [ingredientHistory, setIngredientHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Delete all modal state
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isConfirmingSave, setIsConfirmingSave] = useState(false);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const [savedIngredientsForReturn, setSavedIngredientsForReturn] = useState(null);
  const [showImportConfirmModal, setShowImportConfirmModal] = useState(false);
  const [selectedMenuItemForImport, setSelectedMenuItemForImport] = useState(null);
  const [openUnitIndex, setOpenUnitIndex] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [savedPreparations, setSavedPreparations] = useState([]);
  const [preparationSuggestions, setPreparationSuggestions] = useState([]);
  const [showPreparationCreator, setShowPreparationCreator] = useState(false);

  const normalizeSummaryKey = (value) => String(value || '').trim().toLowerCase();
  const savedPreparationByName = React.useMemo(() => {
    return new Map(
      (savedPreparations || [])
        .filter((prep) => normalizeSummaryKey(prep?.composite_name || prep?.name))
        .map((prep) => [normalizeSummaryKey(prep.composite_name || prep?.name), prep])
    );
  }, [savedPreparations]);

  // Swipe-to-delete state for mobile
  const [swipeOffsets, setSwipeOffsets] = useState({});
  const [swipingIndex, setSwipingIndex] = useState(null);
  const swipeStateRef = useRef({});

  // Load ingredients from database when item is selected
  useEffect(() => {
    if (item) {
      loadIngredientsFromDatabase(item);
    }
  }, [item]);

  // Fetch inventory items and recipes for autocomplete/import suggestions
  useEffect(() => {
    const fetchEditorSuggestions = async () => {
      try {
        const businessId = UnitMappingService.getBusinessId();
        const [items, recipes, libList] = await Promise.all([
          InventoryService.searchInventoryItems('', businessId, 500),
          RecipesService.getRecipes(businessId),
          RecipeLibraryService.list()
        ]);
        setInventoryItems(items || []);
        setAvailableRecipes(recipes || []);
        setRecipeLibraryList(libList || []);
      } catch (error) {
        console.error('Error fetching autocomplete/import suggestions:', error);
      }
    };
    fetchEditorSuggestions();
  }, []);

  const loadSavedPreparations = React.useCallback(async () => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      if (!businessId) return;

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';
      const response = await fetch(`${apiUrl}/medium-features/decomposition/templates?businessId=${businessId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (!response.ok) return;

      const result = await response.json();
      setSavedPreparations(Array.isArray(result?.personalized) ? result.personalized : []);
    } catch (error) {
      console.warn('Failed to load saved preparations for summary:', error);
    }
  }, []);

  useEffect(() => {
    loadSavedPreparations();
  }, [loadSavedPreparations]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }

      if (plateMenuRef.current && !plateMenuRef.current.contains(e.target)) {
        setShowPlateMenu(false);
        setShowCustomPlateInput(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isCustomPlateStep = !presetPlateSteps.includes(Number(plateStep));

  const applyPlateStepChange = (nextStep) => {
    const normalized = Number(nextStep);
    if (!Number.isFinite(normalized) || normalized <= 0) {
      setValidationError('Please enter a valid positive number for plates');
      return;
    }

    const oldStep = Number(plateStep) > 0 ? Number(plateStep) : 1;
    const factor = normalized / oldStep;

    // Scale current ingredients
    setIngredients(prev => prev.map(item => ({
      ...item,
      quantity: item.quantity ? Number((Number(item.quantity) * factor).toFixed(4)) : item.quantity
    })));

    // Scale original ingredients ref (so unsaved changes comparison remains correct)
    if (originalIngredientsRef.current) {
      originalIngredientsRef.current = originalIngredientsRef.current.map(item => ({
        ...item,
        quantity: item.quantity ? Number((Number(item.quantity) * factor).toFixed(4)) : item.quantity
      }));
    }

    // Scale formData if editing
    if (editingIndex !== null && editingIndex !== undefined) {
      setFormData(prev => ({
        ...prev,
        quantity: prev.quantity ? Number((Number(prev.quantity) * factor).toFixed(4)) : prev.quantity
      }));
    }

    setPlateStep(normalized);
    if (!presetPlateSteps.includes(normalized)) {
      setCustomPlateStep(String(normalized));
    }
    setShowCustomPlateInput(false);
    setShowPlateMenu(false);
  };

  const applyCustomPlateStep = () => {
    const parsed = Number(customPlateStep);
    if (isNaN(parsed) || parsed <= 0) {
      setValidationError('Please enter a valid positive number for plates');
      return;
    }
    applyPlateStepChange(parsed);
  };

  // 🔄 Internal auto-save status update (NO DATABASE - just UI feedback)
  useEffect(() => {
    // Skip on initial load / item switch
    if (ingredientsInitRef.current) {
      ingredientsInitRef.current = false;
      return;
    }
    if (!menuItemId) return;
    // Only show status if there are valid ingredients
    const valid = ingredients.filter(i => (i.name || '').trim() && i.name !== 'New Ingredient' && i.quantity > 0);
    if (valid.length === 0) return;
    // Debounce — show "saved" status after 1.5 seconds of inactivity
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      showInternalSaveStatus();
    }, 1500);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [ingredients]);

  // Reset init ref when item changes so first load doesn't auto-save
  useEffect(() => {
    ingredientsInitRef.current = true;
  }, [menuItemId]);

  const getFilteredInventorySuggestions = (value) => {
    const term = String(value || '').trim().toLowerCase();
    return sortBySearchPriority(
      inventoryItems,
      term,
      (inventoryItem) => inventoryItem?.name || '',
      { limit: 50 }
    );
  };

  const getFilteredPreparationSuggestions = (value) => {
    const term = String(value || '').trim().toLowerCase();
    const savedPrepSuggestions = (savedPreparations || []).map(prep => {
      const name = prep.composite_name || prep.name || '';
      const standardUnit = prep.total_weight_unit_symbol || prep.total_weight_unit_name || 'g';
      return {
        id: prep.id ? `prep-${prep.id}` : `prep-${name}`,
        name: name,
        standardUnit: standardUnit
      };
    });
    return sortBySearchPriority(
      savedPrepSuggestions,
      term,
      (prep) => prep?.name || '',
      { limit: 50 }
    );
  };

  const getFilteredMenuSuggestions = (value) => {
    const term = String(value || '').trim().toLowerCase();
    const currentItemName = String(typeof item === 'string' ? item : item?.name || '').trim().toLowerCase();

    const source = (availableRecipes || []).filter((recipe) => {
      const recipeName = String(recipe?.name || '').trim().toLowerCase();
      return recipeName && recipeName !== currentItemName;
    });

    return sortBySearchPriority(
      source,
      term,
      (recipe) => recipe?.name || '',
      { limit: 50 }
    );
  };

  const getFilteredLibrarySuggestions = (value, menuResults) => {
    const term = String(value || '').trim().toLowerCase();
    const currentItemName = String(typeof item === 'string' ? item : item?.name || '').trim().toLowerCase();
    const menuNameSet = new Set((menuResults || []).map(m => String(m?.name || '').toLowerCase().trim()));

    const source = (recipeLibraryList || []).filter((r) => {
      const n = String(r.item_name || r.name || '').toLowerCase().trim();
      return n && n !== currentItemName && !menuNameSet.has(n);
    });

    return sortBySearchPriority(
      source,
      term,
      (r) => r.item_name || r.name || '',
      { limit: 50 }
    );
  };

  const handleNameChange = (value) => {
    setFormData({ ...formData, name: value });
    const filteredInventory = getFilteredInventorySuggestions(value);
    const filteredPreps = getFilteredPreparationSuggestions(value);
    const filteredMenus = getFilteredMenuSuggestions(value);
    const filteredLib = getFilteredLibrarySuggestions(value, filteredMenus);
    setSuggestions(filteredInventory);
    setPreparationSuggestions(filteredPreps);
    setMenuSuggestions(filteredMenus);
    setLibrarySuggestions(filteredLib);
    setShowSuggestions(true);
  };

  const handleNameFocus = () => {
    const filteredInventory = getFilteredInventorySuggestions(formData.name);
    const filteredPreps = getFilteredPreparationSuggestions(formData.name);
    const filteredMenus = getFilteredMenuSuggestions(formData.name);
    const filteredLib = getFilteredLibrarySuggestions(formData.name, filteredMenus);
    setSuggestions(filteredInventory);
    setPreparationSuggestions(filteredPreps);
    setMenuSuggestions(filteredMenus);
    setLibrarySuggestions(filteredLib);
    setShowSuggestions(true);
  };

  const selectSuggestion = (invItem) => {
    const unit = invItem.standardUnit || 'g';
    setFormData({ ...formData, name: invItem.name, unit: unit });
    setShowSuggestions(false);
    setSuggestions([]);
    setPreparationSuggestions([]);
    setMenuSuggestions([]);
    setTimeout(() => {
      if (quantityInputRef.current) {
        quantityInputRef.current.focus();
        quantityInputRef.current.select();
      }
    }, 100);
  };

  const selectMenuSuggestion = (menuItem) => {
    setSelectedMenuItemForImport(menuItem);
    setShowImportConfirmModal(true);
  };

  const selectLibrarySuggestion = (recipe) => {
    setSelectedMenuItemForImport({ name: recipe.item_name || recipe.name, source: 'library' });
    setShowImportConfirmModal(true);
  };

  // Undo/Redo helper functions
  const cloneIngredientList = (list) => list.map(item => ({ ...item }));
  
  const pushIngredientHistory = (previousList, newList) => {
    const nextHistory = ingredientHistory.slice(0, historyIndex + 1);
    nextHistory.push({ previousList, newList });
    setIngredientHistory(nextHistory);
    setHistoryIndex(nextHistory.length - 1);
  };
  
  const handleUndo = () => {
    if (historyIndex < 0) return;
    const entry = ingredientHistory[historyIndex];
    if (entry) {
      setIngredients(cloneIngredientList(entry.previousList));
      setHistoryIndex(historyIndex - 1);
    }
  };
  
  const handleRedo = () => {
    if (historyIndex >= ingredientHistory.length - 1) return;
    const entry = ingredientHistory[historyIndex + 1];
    if (entry) {
      setIngredients(cloneIngredientList(entry.newList));
      setHistoryIndex(historyIndex + 1);
    }
  };
  
  const handleDeleteAll = () => {
    const prevList = cloneIngredientList(ingredients);
    setIngredients([]);
    pushIngredientHistory(prevList, []);
    setShowDeleteAllModal(false);
    setEditingIndex(null);
    setFormData({ name: '', quantity: 1, unit: 'g' });
  };

  const confirmImportIngredients = async () => {
    if (!selectedMenuItemForImport) return;

    try {
      const businessId = UnitMappingService.getBusinessId();

      let normalizedImportedIngredients;

      if (selectedMenuItemForImport.source === 'library') {
        // Fetch from Recipe Library (has real weights)
        const recipe = await RecipeLibraryService.getRecipe(selectedMenuItemForImport.name, businessId);
        const libIngredients = recipe?.ingredients || [];

        if (!libIngredients.length) {
          setShowImportConfirmModal(false);
          setSelectedMenuItemForImport(null);
          return;
        }

        normalizedImportedIngredients = libIngredients
          .map((ing) => ({
            name: ing.name || '',
            quantity: Number(ing.quantity) || 0,
            unit: ing.unit || 'g',
            item_id: null,
            unit_id: null,
            notes: ''
          }))
          .filter((ingredient) => ingredient.name.trim() && ingredient.quantity > 0);
      } else {
        // Fetch from existing menu item recipe
        const recipeId = selectedMenuItemForImport.id || selectedMenuItemForImport.menu_item_id;
        const importedIngredients = await RecipesService.getRecipeIngredients(recipeId, businessId);

        if (!importedIngredients || importedIngredients.length === 0) {
          setShowImportConfirmModal(false);
          setSelectedMenuItemForImport(null);
          return;
        }

        normalizedImportedIngredients = importedIngredients
          .map((ingredient) => ({
            name: ingredient.ingredient_name || ingredient.item_name || ingredient.name || '',
            quantity: Number(ingredient.quantity) || 0,
            unit: ingredient.unit || ingredient.unit_symbol || 'g',
            item_id: ingredient.item_id || ingredient.inventory_id || null,
            unit_id: ingredient.unit_id || null,
            notes: ingredient.notes || ''
          }))
          .filter((ingredient) => ingredient.name.trim() && ingredient.quantity > 0);
      }

      const importedMap = new Map();
      normalizedImportedIngredients.forEach((ingredient) => {
        importedMap.set(ingredient.name.toLowerCase().trim(), ingredient);
      });

      const currentNames = new Set(
        ingredients.map((ingredient) => ingredient.name.toLowerCase().trim())
      );

      const nonUpdatedItems = ingredients.filter((ingredient) => {
        const key = ingredient.name.toLowerCase().trim();
        return !importedMap.has(key);
      });

      const updatedItems = normalizedImportedIngredients.filter((ingredient) => {
        const key = ingredient.name.toLowerCase().trim();
        return currentNames.has(key);
      });

      const newItems = normalizedImportedIngredients.filter((ingredient) => {
        const key = ingredient.name.toLowerCase().trim();
        return !currentNames.has(key);
      });

      const finalList = [...nonUpdatedItems, ...updatedItems, ...newItems];
      const previousList = cloneIngredientList(ingredients);

      setIngredients(finalList);
      pushIngredientHistory(previousList, cloneIngredientList(finalList));
      setShowImportConfirmModal(false);
      setSelectedMenuItemForImport(null);
      setShowSuggestions(false);
      setSuggestions([]);
      setMenuSuggestions([]);
      setLibrarySuggestions([]);
      setEditingIndex(null);
      setOpenUnitIndex(null);
      setFormData({ name: '', quantity: 1, unit: 'g' });
    } catch (error) {
      console.error('Failed to import ingredients:', error);
      setShowImportConfirmModal(false);
      setSelectedMenuItemForImport(null);
    }
  };

  const loadIngredientsFromDatabase = async (itemData) => {
    try {
      setLoading(true);
      const businessId = UnitMappingService.getBusinessId();

      // Check if this is an advanced ComplimentaryRecipe (has comp_recipe_id)
      if (itemData.comp_recipe_id) {

        // Use FeaturesService to get the complimentary recipe
        const result = await FeaturesService.getComplimentaryRecipeById(itemData.comp_recipe_id);

        if (result.success && result.complimentaryRecipe) {
          const recipeData = result.complimentaryRecipe;
          setMenuItemId(itemData.comp_recipe_id); // Store comp_recipe_id for saving
          setImageUrl(recipeData.image_url || null);

          // Set portion size
          const loadedPortionSize = Number(recipeData.portion_size) > 0 ? Number(recipeData.portion_size) : 1.0;
          setPlateStep(loadedPortionSize);
          if (!presetPlateSteps.includes(loadedPortionSize)) {
            setCustomPlateStep(String(loadedPortionSize));
          } else {
            setCustomPlateStep('');
          }
          setShowPlateMenu(false);
          setShowCustomPlateInput(false);

          // Get ingredients from ComplimentaryRecipeIngredients
          // Service returns: { item_id, item_name, quantity, unit_id, unit_symbol, notes }
          // We need to map to: { name, quantity, unit }
          if (recipeData.ingredients && recipeData.ingredients.length > 0) {
            const mappedIngredients = recipeData.ingredients.map(ing => {
              // Auto-scale to the app-wide readable unit (0.01 kg -> 10 g, 1 kg stays kg),
              // keeping unit + unit_id consistent so a later save persists the same physical amount.
              const scaled = autoScaleToSymbol(ing.quantity, ing.unit_symbol || ing.unit_name);
              return {
                name: ing.item_name,
                quantity: scaled.qty,
                unit: scaled.unit,
                item_id: ing.item_id,      // Keep for reference
                unit_id: UNIT_SYMBOL_TO_ID[scaled.unit] || ing.unit_id,  // keep unit_id aligned to the scaled unit
                notes: ing.notes || ''
              };
            });

            setIngredients(mappedIngredients);
            // Store original for revert on cancel
            originalIngredientsRef.current = JSON.parse(JSON.stringify(mappedIngredients));
          } else {
            setIngredients([]);
            originalIngredientsRef.current = [];
          }
        } else {
          setIngredients([]);
        }
      } else {
        // Legacy system: Load from MenuItems (using RecipesService like itemmap.js)
        const itemName = itemData.name || itemData;

        // First, find the menu item ID for this complimentary item
        const allRecipes = await RecipesService.getRecipes(businessId);
        const menuItem = (allRecipes || []).find(r =>
          r.name.toLowerCase().trim() === itemName.toLowerCase().trim()
        );

        if (menuItem && (menuItem.id || menuItem.menu_item_id)) {
          const recipeId = menuItem.id || menuItem.menu_item_id;
          setMenuItemId(recipeId);
          setImageUrl(menuItem.image_url || menuItem.image);

          setPlateStep(1.0);
          setCustomPlateStep('');
          setShowPlateMenu(false);
          setShowCustomPlateInput(false);

          // Fetch ingredients for this menu item
          const ingredientsData = await RecipesService.getRecipeIngredients(recipeId, businessId);

          if (ingredientsData && ingredientsData.length > 0) {
            setIngredients(ingredientsData);
            // Store original for revert on cancel
            originalIngredientsRef.current = JSON.parse(JSON.stringify(ingredientsData));
          } else {
            loadDefaultIngredients(itemName);
          }
        } else {
          loadDefaultIngredients(itemName);
        }
      }
    } catch (error) {
      console.error('❌ Error loading ingredients:', error);
      const itemName = typeof item === 'string' ? item : item.name;
      loadDefaultIngredients(itemName);
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultIngredients = (itemName) => {
    // All complimentary items are now stored in database (Sides category)
    originalIngredientsRef.current = [];
    // No hardcoded defaults needed anymore
    setIngredients([]);
  };

  useEffect(() => {
    if (editingIndex === null || editingIndex === undefined) return;

    const timer = setTimeout(() => {
      const filteredInventory = getFilteredInventorySuggestions(formData.name);
      const filteredMenus = getFilteredMenuSuggestions(formData.name);
      const filteredLib = getFilteredLibrarySuggestions(formData.name, filteredMenus);
      setSuggestions(filteredInventory);
      setMenuSuggestions(filteredMenus);
      setLibrarySuggestions(filteredLib);

      if (editNameInputRef.current) {
        editNameInputRef.current.focus();
        const len = editNameInputRef.current.value.length;
        editNameInputRef.current.setSelectionRange(len, len);
      }

      if (!formData.name?.trim()) {
        setShowSuggestions(true);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [editingIndex]);

  const startEditing = (index) => {
    const ingredient = ingredients[index];
    const ingredientName = ingredient.name === 'New Ingredient' ? '' : (ingredient.name || '');
    setFormData({
      name: ingredientName,
      quantity: ingredient.quantity,
      unit: ingredient.unit
    });
    setOpenUnitIndex(null);
    setEditingIndex(index);
    if (!ingredientName || ingredientName === 'New Ingredient') {
      setShowSuggestions(true);
    }
  };

  const cancelEditing = () => {
    // If canceling a new empty ingredient, remove it
    if (editingIndex !== null && (ingredients[editingIndex]?.name === '' || ingredients[editingIndex]?.name === 'New Ingredient')) {
      const updated = ingredients.filter((_, idx) => idx !== editingIndex);
      setIngredients(updated);
    }
    setEditingIndex(null);
    setOpenUnitIndex(null);
    setFormData({ name: '', quantity: 1, unit: 'g' });
    setShowSuggestions(false);
    setSuggestions([]);
    setPreparationSuggestions([]);
    setMenuSuggestions([]);
    setLibrarySuggestions([]);
  };

  const saveIngredient = (opts = {}) => {
    const { silent = false, returnUpdatedList = false } = opts;
    if (!formData.name.trim() || formData.name.trim() === 'New Ingredient') {
      if (!silent) setValidationError('Please enter an ingredient name');
      return returnUpdatedList ? null : false;
    }
    if (!formData.quantity || Number(formData.quantity) <= 0) {
      if (!silent) setValidationError('Please enter a valid quantity');
      return returnUpdatedList ? null : false;
    }
    if (!formData.unit || !String(formData.unit).trim()) {
      if (!silent) setValidationError('Please select a unit');
      return returnUpdatedList ? null : false;
    }
    if (editingIndex === null || editingIndex === undefined) {
      return returnUpdatedList ? cloneIngredientList(ingredients) : true;
    }
    
    const prevList = cloneIngredientList(ingredients);
    const updated = [...ingredients];
    updated[editingIndex] = {
      ...updated[editingIndex],
      name: formData.name,
      quantity: Number(formData.quantity),
      unit: formData.unit
    };
    setIngredients(updated);
    pushIngredientHistory(prevList, cloneIngredientList(updated));
    setEditingIndex(null);
    setOpenUnitIndex(null);
    setFormData({ name: '', quantity: 1, unit: 'g' });
    setShowSuggestions(false);
    return returnUpdatedList ? updated : true;
  };

  const updateQuantity = (index, delta) => {
    const prevList = cloneIngredientList(ingredients);
    const updated = [...ingredients];
    const newQty = Math.max(0, updated[index].quantity + delta);
    updated[index].quantity = newQty;
    setIngredients(updated);
    pushIngredientHistory(prevList, cloneIngredientList(updated));
  };

  const handleQuantityChange = (index, value) => {
    const updated = [...ingredients];
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue >= 0) {
      updated[index].quantity = numValue;
      setIngredients(updated);
    }
  };

  const handleUnitChange = (index, newUnit) => {
    const prevList = cloneIngredientList(ingredients);
    const updated = [...ingredients];
    updated[index].unit = newUnit;
    setIngredients(updated);
    pushIngredientHistory(prevList, cloneIngredientList(updated));
  };

  // Swipe-to-delete handlers for mobile
  const handleRowTouchStart = (index, event) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    const target = event.target;
    const isControl = target.closest(
      '.comps-controls-inline, .comps-number-input, .comps-text-input, .comps-square-btn, [role="button"], input, select, button, .comps-suggestions'
    );
    if (isControl) return;
    const touch = event.touches[0];
    setSwipingIndex(index);
    swipeStateRef.current = {
      index,
      startX: touch.clientX,
      startY: touch.clientY,
      endX: touch.clientX,
      endY: touch.clientY
    };
  };

  const handleRowTouchMove = (index, event) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    if (swipeStateRef.current.index !== index) return;
    const touch = event.touches[0];
    swipeStateRef.current.endX = touch.clientX;
    swipeStateRef.current.endY = touch.clientY;
    const deltaX = touch.clientX - swipeStateRef.current.startX;
    const bounded = Math.max(-120, Math.min(120, deltaX));
    setSwipeOffsets(prev => ({ ...prev, [index]: bounded }));
  };

  const handleRowTouchEnd = (index) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    const swipe = swipeStateRef.current;
    if (swipe.index !== index) return;
    const deltaX = swipe.endX - swipe.startX;
    const deltaY = swipe.endY - swipe.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const shouldDelete = absX > 60 && absX > absY * 1.5;
    if (shouldDelete && editingIndex !== index) {
      const exitOffset = deltaX > 0 ? 160 : -160;
      setSwipeOffsets(prev => ({ ...prev, [index]: exitOffset }));
      setTimeout(() => {
        deleteIngredient(index);
        setSwipeOffsets(prev => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
      }, 120);
    } else {
      setSwipeOffsets(prev => ({ ...prev, [index]: 0 }));
    }
    setSwipingIndex(null);
    swipeStateRef.current = {};
  };

  const addNewIngredient = () => {
    // Auto-save current ingredient if it has valid data
    if (editingIndex !== null) {
      const hasValidName = formData.name && formData.name.trim() && formData.name.trim() !== '';
      if (hasValidName) {
        saveIngredient();
      } else {
        // Remove the unsaved empty ingredient
        const cleaned = ingredients.filter((ing, idx) => idx !== editingIndex || ing.name !== '');
        setIngredients(cleaned);
        setEditingIndex(null);
        setOpenUnitIndex(null);
        setFormData({ name: '', quantity: 1, unit: 'g' });
      }
    }
    
    // Add new ingredient after a short delay
    setTimeout(() => {
      setIngredients(prev => {
        const newIngredient = { name: 'New Ingredient', quantity: 1, unit: 'g' };
        const updatedIngredients = [...prev, newIngredient];
        setEditingIndex(updatedIngredients.length - 1);
        setOpenUnitIndex(null);
        setFormData({ name: '', quantity: 1, unit: 'g' });
        setShowSuggestions(true);
        return updatedIngredients;
      });
    }, 50);
  };

  const deleteIngredient = (index) => {
    const prevList = cloneIngredientList(ingredients);
    const updated = ingredients.filter((_, idx) => idx !== index);
    setIngredients(updated);
    pushIngredientHistory(prevList, cloneIngredientList(updated));
    if (editingIndex === index) {
      setEditingIndex(null);
      setOpenUnitIndex(null);
      setFormData({ name: '', quantity: 1, unit: 'g' });
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const unitOptions = [
    { value: 'g', label: 'g' },
    { value: 'kg', label: 'kg' },
    { value: 'ml', label: 'ml' },
    { value: 'L', label: 'L' },
    { value: 'pcs', label: 'pcs' }
  ];

  // Helper function to lookup item_id from inventory by name (using authenticated InventoryService)
  const lookupItemId = async (ingredientName) => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      const items = await InventoryService.searchInventoryItems('', businessId, 500);
      // Find exact match (case-insensitive)
      const match = (items || []).find(item =>
        item.name?.toLowerCase() === ingredientName.toLowerCase()
      );

      if (match) {
        return match.id;
      } else {
        console.warn(`⚠️ No exact match found for ingredient: "${ingredientName}"`);
        return null;
      }
    } catch (error) {
      console.error('Error looking up item:', error);
      return null;
    }
  };

  // Helper function to lookup unit_id from GlobalUnits by symbol (direct DB query like inventory.js)
  const lookupUnitId = async (unitSymbol) => {
    try {
      const unitData = await UnitMappingService.lookupUnitBySymbol(unitSymbol);
      if (unitData?.unit_id) {
        return unitData.unit_id;
      }

      console.error(`❌ Unit not found: "${unitSymbol}"`);
      return null;
    } catch (error) {
      console.error('Error looking up unit:', error);
      return null;
    }
  };

  // Internal save status: Just show UI feedback (NO DATABASE SAVE)
  const showInternalSaveStatus = () => {
    setAutoSaveStatus('saving');
    setTimeout(() => {
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 1500);
    }, 300);
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    const current = JSON.stringify(ingredients.map(i => ({ name: i.name, quantity: i.quantity, unit: i.unit })));
    const original = JSON.stringify(originalIngredientsRef.current.map(i => ({ name: i.name, quantity: i.quantity, unit: i.unit })));
    return current !== original;
  };

  const discardUnsavedChangesAndGoBack = () => {
    setIngredients(JSON.parse(JSON.stringify(originalIngredientsRef.current)));
    setEditingIndex(null);
    setShowSuggestions(false);
    setOpenUnitIndex(null);
    setShowUnsavedChangesModal(false);

    if (onBack) onBack();
  };

  // Handle back button: revert changes if not saved
  const handleBackButton = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesModal(true);
    } else {
      if (onBack) onBack();
    }
  };

  const handleSaveSuccessOk = async () => {
    const savedIngredients = savedIngredientsForReturn;

    setShowSaveSuccessModal(false);
    setSavedIngredientsForReturn(null);

    if (onSave) {
      await onSave(savedIngredients || ingredients);
      return;
    }

    if (onBack) onBack();
  };

  const handleSave = async () => {
    if (isConfirmingSave) return;

    setIsConfirmingSave(true);

    try {
      let ingredientsForSave = ingredients;

      if (editingIndex !== null && editingIndex !== undefined) {
        const savedList = saveIngredient({ silent: true, returnUpdatedList: true });
        if (!savedList) {
          setValidationError('Please complete the current ingredient before confirming');
          return;
        }
        ingredientsForSave = savedList;
      }

      const rawPlateCount = Number(plateStep) > 0 ? Number(plateStep) : 1;
      const normalizedPlateCount = rawPlateCount < 1 ? 1 : rawPlateCount;
      const dbPortionSize = rawPlateCount < 1 ? rawPlateCount : 1.0;

      const ingredientsToPersist = (Array.isArray(ingredientsForSave) ? ingredientsForSave : [])
        .filter(isPersistableIngredient)
        .map(ing => ({ 
          ...ing, 
          quantity: Number((Number(ing.quantity) / normalizedPlateCount).toFixed(4)) 
        }));

      if (ingredientsToPersist.length === 0) {
        setValidationError('Add at least one ingredient quantity before confirming');
        return;
      }

      const businessId = UnitMappingService.getBusinessId();

      if (menuItemId) {
        // Check if this is an advanced ComplimentaryRecipe (comp_recipe_id stored in menuItemId)
        if (item.comp_recipe_id) {
        
          // Convert ingredient names and unit symbols to IDs
          const ingredientsWithIds = [];
          for (const ing of ingredientsToPersist) {
            const ingredientName = ing.name || ing.ingredient_name;
            const unitSymbol = ing.unit;

            // 🆕 AUTO-CREATE inventory item if not found (matches itemmap.js behavior)
            let itemId = await lookupItemId(ingredientName);
            if (!itemId) {
              try {
                const createdItem = await InventoryService.createInventoryItem({
                  name: ingredientName,
                  unit_symbol: unitSymbol,
                  business_id: businessId,
                  source: 'complimentary_mapping'
                });

                // Use the returned item_id directly instead of looking it up again
                if (createdItem && createdItem.item_id) {
                  itemId = createdItem.item_id;
                } else {
                  // Fallback: wait briefly and lookup again
                  await new Promise(resolve => setTimeout(resolve, 500));
                  itemId = await lookupItemId(ingredientName);
                }
              } catch (createError) {
                const status = createError?.status || createError?.response?.status;
                const data = createError?.data || createError?.response?.data || {};
                const message = String(data?.error || data?.message || data?.details || createError?.message || '').toLowerCase();
                const isDuplicate = status === 409 || message.includes('already') || message.includes('exists') || message.includes('duplicate') || message.includes('unique constraint');

                if (isDuplicate) {
                  itemId = await lookupItemId(ingredientName);
                }

                if (!itemId) {
                  console.error(`Failed to create inventory item: ${ingredientName}`, createError);
                  setValidationError(`Failed to create inventory item: "${ingredientName}". ${createError.message || 'Unknown error'}`);
                  return;
                }
              }
            }

            // Final validation after auto-create attempt
            if (!itemId) {
              setValidationError(`Cannot find or create inventory item: "${ingredientName}". Please try again.`);
              return;
            }

            const unitId = await lookupUnitId(unitSymbol);
            if (!unitId) {
              setValidationError(`Cannot find unit: "${unitSymbol}". Please check the unit symbol.`);
              return;
            }

            ingredientsWithIds.push({
              itemId: itemId,
              quantity: ing.quantity,
              unitId: unitId,
              notes: ing.notes || ''
            });
          }

          // Use FeaturesService to update ComplimentaryRecipe
          const result = await FeaturesService.updateComplimentaryRecipe(menuItemId, {
            ingredients: ingredientsWithIds,
            portionSize: dbPortionSize
          });

          if (result.success) {
            // Update original ref so we don't get "unsaved changes" alert
            originalIngredientsRef.current = JSON.parse(JSON.stringify(ingredientsToPersist));
            setIngredients(ingredientsToPersist);
            
            // Reset plate step to saved portion size
            setPlateStep(dbPortionSize);
            if (!presetPlateSteps.includes(dbPortionSize)) {
              setCustomPlateStep(String(dbPortionSize));
            } else {
              setCustomPlateStep('');
            }
            
            setSavedIngredientsForReturn(ingredientsToPersist);
            setShowSaveSuccessModal(true);
          } else {
            console.error('❌ Failed to save ingredients to ComplimentaryRecipes');
            setValidationError('Failed to save ingredients. Please try again.');
          }
        } else {
          // Legacy system: Save to MenuItems (using authenticated httpClient)
          const result = await http.put(`/recipes/${menuItemId}/ingredients`, { ingredients: ingredientsToPersist });

          if (result && result.success !== false) {
            // Update original ref so we don't get "unsaved changes" alert
            originalIngredientsRef.current = JSON.parse(JSON.stringify(ingredientsToPersist));
            setIngredients(ingredientsToPersist);
            
            setPlateStep(dbPortionSize);
            if (!presetPlateSteps.includes(dbPortionSize)) {
              setCustomPlateStep(String(dbPortionSize));
            } else {
              setCustomPlateStep('');
            }
            
            setSavedIngredientsForReturn(ingredientsToPersist);
            setShowSaveSuccessModal(true);
          } else {
            console.error('❌ Failed to save ingredients');
            setValidationError('Failed to save ingredients. Please try again.');
          }
        }
      } else {
        console.warn('⚠️ No menu item ID, calling onSave callback only');
        setIngredients(ingredientsToPersist);
        originalIngredientsRef.current = JSON.parse(JSON.stringify(ingredientsToPersist));
        
        setPlateStep(dbPortionSize);
        if (!presetPlateSteps.includes(dbPortionSize)) {
          setCustomPlateStep(String(dbPortionSize));
        } else {
          setCustomPlateStep('');
        }
        
        setSavedIngredientsForReturn(ingredientsToPersist);
        setShowSaveSuccessModal(true);
      }
    } catch (error) {
      console.error('❌ Error saving ingredients:', error);
      setValidationError('Error saving ingredients. Please try again.');
    } finally {
      setIsConfirmingSave(false);
    }
  };

  if (!item) return null;

  if (loading) {
    return (
      <div className="comps-page-container">
        <div className="comps-page-content">
          <div className="comps-loading-state">
            <div className="comps-loading-brand">
              <img
                src="/invexis-logo.png"
                alt="Invexis"
                className="comps-loading-logo"
              />
              <div className="comps-loading-ring"></div>
            </div>
            <p className="comps-loading-text">Loading ingredients...</p>
          </div>
        </div>
      </div>
    );
  }

  const itemName = typeof item === 'string' ? item : item.name;
  const itemDisplayName = toTitleCase(itemName);

  return (
    <div className="comps-page-container">
      <div className="comps-page-content">
        {/* Header - matching itemmap style */}
        <div className="comps-page-header">
          <button
            onClick={handleBackButton}
            className="comps-back-arrow-btn"
            aria-label="Back"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h3 className="comps-page-title">Complimentary Items</h3>
        </div>

        {/* Recipe name row - matching itemmap */}
        <div className="comps-recipe-titlebar">
          <div className="comps-recipe-title">
            {itemDisplayName}
          </div>
          <div ref={plateMenuRef} className="comps-plate-menu-wrap">
            <button
              type="button"
              className="comps-plate-menu-button"
              onClick={() => setShowPlateMenu((previous) => !previous)}
              aria-haspopup="menu"
              aria-expanded={showPlateMenu}
            >
              <span className="comps-plate-menu-icon">☰</span>
              <span>{plateStep} {plateStep === 1 ? 'plate' : 'plates'}</span>
            </button>

            {showPlateMenu && (
              <div className="comps-plate-menu-dropdown" role="menu">
                {[1, 10, 100].map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={`comps-plate-menu-item ${plateStep === size ? 'is-active' : ''}`}
                    onClick={() => applyPlateStepChange(size)}
                    role="menuitemradio"
                    aria-checked={plateStep === size}
                  >
                    {size} {size === 1 ? 'plate' : 'plates'}
                  </button>
                ))}
                <button
                  type="button"
                  className={`comps-plate-menu-item ${isCustomPlateStep ? 'is-active' : ''}`}
                  onClick={() => {
                    setShowCustomPlateInput(true);
                    if (isCustomPlateStep) {
                      setCustomPlateStep(String(plateStep));
                    }
                  }}
                  role="menuitemradio"
                  aria-checked={isCustomPlateStep}
                  style={{ borderTop: '1px solid #f3f4f6' }}
                >
                  {isCustomPlateStep ? `${plateStep} plates (custom)` : 'Custom plates'}
                </button>
                {showCustomPlateInput && (
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6', background: '#fafafa' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="number"
                        min="0.01"
                        step="any"
                        inputMode="decimal"
                        value={customPlateStep}
                        onChange={(e) => setCustomPlateStep(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') applyCustomPlateStep();
                        }}
                        placeholder="Enter plates (e.g. 0.5)"
                        style={{
                          flex: 1,
                          height: '32px',
                          padding: '0 10px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '13px',
                          fontWeight: '600',
                          background: '#fff'
                        }}
                      />
                      <button
                        type="button"
                        onClick={applyCustomPlateStep}
                        style={{
                          height: '32px',
                          padding: '0 12px',
                          borderRadius: '6px',
                          border: '1px solid #111',
                          background: '#111',
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Image section - matching itemmap hero (always show with fallback) */}
        <div className="comps-recipe-hero-shell">
          <img
            src={imageUrl || 'https://storage.googleapis.com/menuimages001/itemmap/default.png'}
            alt={itemDisplayName}
            className="comps-recipe-hero"
          />
          {/* Custom Image Upload Button */}
          <CustomImageUploadModal
            isOpen={true}
            recipeId={menuItemId}
            recipeName={itemName}
            currentImageUrl={imageUrl}
            showCameraButton={true}
            onUploadSuccess={({ imageUrl: newImageUrl }) => {
              setImageUrl(newImageUrl);
            }}
            onRevertToAI={() => {
              setImageUrl('https://storage.googleapis.com/menuimages001/itemmap/default.png');
            }}
          />
        </div>

        {/* Ingredients Panel - matching itemmap exactly */}
        <div className="comps-panel" style={{ marginTop: 16 }}>
          {/* Ingredients heading + actions in same row */}
          <div className="comps-toolbar-row">
            <h4 className="comps-panel-title comps-panel-title--inline">Ingredients</h4>

            <div className="comps-toolbar-actions">
              <button
                onClick={handleUndo}
                disabled={historyIndex < 0}
                className={`comps-toolbar-action-btn ${historyIndex < 0 ? 'is-disabled' : ''}`}
                title="Undo"
                type="button"
              >
                <FontAwesomeIcon icon={faUndo} style={{ fontSize: 12 }} />
              </button>
              <button
                onClick={handleRedo}
                disabled={historyIndex >= ingredientHistory.length - 1}
                className={`comps-toolbar-action-btn ${historyIndex >= ingredientHistory.length - 1 ? 'is-disabled' : ''}`}
                title="Redo"
                type="button"
              >
                <FontAwesomeIcon icon={faRedo} style={{ fontSize: 12 }} />
              </button>
              <button
                onClick={() => setShowDeleteAllModal(true)}
                disabled={ingredients.length === 0}
                className={`comps-toolbar-action-btn comps-toolbar-action-btn--danger ${ingredients.length === 0 ? 'is-disabled' : ''}`}
                title="Delete All Ingredients"
                type="button"
              >
                <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
              </button>
              <button
                onClick={() => {
                  console.log('🆕 Create Preparation button clicked');
                  setShowPreparationCreator(true);
                }}
                className="comps-toolbar-action-btn comps-toolbar-action-btn--success"
                title="Create Preparation"
                type="button"
              >
                <FontAwesomeIcon icon={faPlus} style={{ fontSize: 12 }} />
              </button>
            </div>
          </div>

          {/* Container for ingredient rows - matching itemmap exactly */}
          <div style={{
            overflow: 'visible',
            marginBottom: '12px',
            position: 'relative',
            zIndex: 1
          }}>
            {ingredients.map((ingredient, idx) => (
                <div
                  key={idx}
                  className={`ingredient-row ${editingIndex === idx ? 'editing' : ''} ${(typeof window !== 'undefined' && window.innerWidth <= 640 && editingIndex !== idx)
                    ? (((ingredient.name || '').trim().length <= 18) ? 'row-inline' : 'row-stacked')
                    : ''} ${Math.abs(swipeOffsets[idx] || 0) > 10 ? 'swipe-active' : ''}`}
                  style={{
                    transform: `translateX(${swipeOffsets[idx] || 0}px)`,
                    transition: swipingIndex === idx ? 'none' : 'transform 0.12s ease'
                  }}
                  onTouchStart={(e) => handleRowTouchStart(idx, e)}
                  onTouchMove={(e) => handleRowTouchMove(idx, e)}
                  onTouchEnd={() => handleRowTouchEnd(idx)}
                >
                  {editingIndex === idx ? (
                    <div
                      ref={editingIndex === idx ? ingredientEditorRef : null}
                      className="itemmap-ingredient-editing-wrapper"
                      style={{ position: 'relative', width: '100%' }}
                    >
                      <div className="itemmap-ingredient-editing-main" style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
                        <div className="itemmap-ingredient-name-input" ref={suggestionsRef} style={{ flex: 1, minWidth: 0, position: 'relative', textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                           <input
                             ref={editNameInputRef}
                             type="text"
                             value={formData.name}
                             onChange={(e) => handleNameChange(e.target.value)}
                             onFocus={() => {
                               handleNameFocus();
                               setShowSuggestions(true);
                             }}
                             onBlur={(e) => {
                               const typed = formData.name?.trim();
                               if (typed && typed !== 'New Ingredient' && editingIndex !== null) {
                                 setIngredients(prev => {
                                   const updated = [...prev];
                                   const row = updated[editingIndex];
                                   if (row && row.name !== typed) {
                                     updated[editingIndex] = { ...row, name: typed };
                                   }
                                   return updated;
                                 });
                               }
                             }}
                             onKeyDown={(e) => {
                               if (e.key === 'Enter') {
                                 e.preventDefault();
                                 const typed = formData.name?.trim();
                                 if (typed && typed !== 'New Ingredient' && editingIndex !== null) {
                                   setIngredients(prev => {
                                     const updated = [...prev];
                                     const row = updated[editingIndex];
                                     if (row && row.name !== typed) {
                                       updated[editingIndex] = { ...row, name: typed };
                                     }
                                     return updated;
                                   });
                                 }
                                 setShowSuggestions(false);
                                 if (quantityInputRef.current) {
                                   quantityInputRef.current.focus();
                                   quantityInputRef.current.select();
                                 }
                               }
                             }}
                             placeholder="Search Ingredient Name"
                             className="text-input"
                             style={{ fontWeight: 500, width: '100%', flex: 1, minWidth: 0, textAlign: 'left', fontSize: '1rem', lineHeight: '1.2', padding: 0, border: 'none', background: 'transparent' }}
                           />
                          </div>
                          {showSuggestions && (() => {
                            const isLastRow = idx === ingredients.length - 1;

                            return (
                              <div
                                className="suggestions"
                                style={{
                                  position: 'absolute',
                                  top: isLastRow ? 'auto' : '100%',
                                  bottom: isLastRow ? '100%' : 'auto',
                                  left: 0,
                                  right: 0,
                                  background: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: 8,
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                  maxHeight: 400,
                                  overflowY: 'auto',
                                  zIndex: 3000,
                                  marginTop: isLastRow ? 0 : 4,
                                  marginBottom: isLastRow ? 4 : 0,
                                  minWidth: '280px',
                                  width: '100%'
                                }}
                              >
                                {suggestions.length > 0 && (
                                  <>
                                    <div style={{
                                      padding: '8px 12px',
                                      background: '#f9fafb',
                                      borderBottom: '1px solid #e5e7eb',
                                      fontWeight: 600,
                                      fontSize: 12,
                                      color: '#666',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}>
                                      <span>Ingredient Names</span>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          cancelEditing();
                                        }}
                                        title="Close suggestions"
                                        style={{
                                          width: 20,
                                          height: 20,
                                          borderRadius: 999,
                                          border: '1px solid #d1d5db',
                                          background: '#fff',
                                          color: '#6b7280',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer',
                                          padding: 0
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                                      </button>
                                    </div>
                                    {suggestions.map((invItem) => (
                                      <div
                                        key={invItem.id}
                                        className="suggestion-item"
                                        onClick={() => selectSuggestion(invItem)}
                                        style={{
                                          padding: '10px 12px',
                                          cursor: 'pointer',
                                          borderBottom: '1px solid #f3f4f6',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                      >
                                        <span style={{ fontWeight: 500 }}>{invItem.name}</span>
                                        {invItem.standardUnit && (
                                          <span className="suggestion-unit" style={{
                                            background: '#f3f4f6',
                                            padding: '2px 8px',
                                            borderRadius: 4,
                                            fontSize: 12,
                                            color: '#666'
                                          }}>{invItem.standardUnit}</span>
                                        )}
                                      </div>
                                    ))}
                                  </>
                                )}

                                {/* Saved Preparations Section */}
                                {preparationSuggestions.length > 0 && (
                                  <>
                                    <div style={{
                                      padding: '8px 12px',
                                      background: '#f9fafb',
                                      borderBottom: '1px solid #e5e7eb',
                                      fontWeight: 600,
                                      fontSize: 12,
                                      color: '#666',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      marginTop: suggestions.length > 0 ? 4 : 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}>
                                      <span>Saved Preparations</span>
                                      {suggestions.length === 0 && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            cancelEditing();
                                          }}
                                          title="Close suggestions"
                                          style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: 999,
                                            border: '1px solid #d1d5db',
                                            background: '#fff',
                                            color: '#6b7280',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            padding: 0
                                          }}
                                        >
                                          <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                                        </button>
                                      )}
                                    </div>
                                    {preparationSuggestions.map((s) => (
                                      <div
                                        key={`prep-${s.id || s.name}`}
                                        className="suggestion-item"
                                        onClick={() => selectSuggestion(s)}
                                        style={{
                                          padding: '10px 12px',
                                          cursor: 'pointer',
                                          borderBottom: '1px solid #f3f4f6',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          transition: 'background 0.2s',
                                          background: 'white'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                      >
                                        <span style={{ fontWeight: 500 }}>{s.name}</span>
                                        {s.standardUnit && (
                                          <span className="suggestion-unit" style={{
                                            background: '#f3f4f6',
                                            padding: '2px 8px',
                                            borderRadius: 4,
                                            fontSize: 12,
                                            color: '#666'
                                          }}>{s.standardUnit}</span>
                                        )}
                                      </div>
                                    ))}
                                  </>
                                )}

                                {menuSuggestions.length > 0 && (
                                  <>
                                    <div style={{
                                      padding: '8px 12px',
                                      background: '#f9fafb',
                                      borderBottom: '1px solid #e5e7eb',
                                      fontWeight: 600,
                                      fontSize: 12,
                                      color: '#666',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      marginTop: (suggestions.length > 0 || preparationSuggestions.length > 0) ? 4 : 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}>
                                      <span>Menu Items (Import All Ingredients)</span>
                                      {suggestions.length === 0 && preparationSuggestions.length === 0 && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            cancelEditing();
                                          }}
                                          title="Close suggestions"
                                          style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: 999,
                                            border: '1px solid #d1d5db',
                                            background: '#fff',
                                            color: '#6b7280',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            padding: 0
                                          }}
                                        >
                                          <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                                        </button>
                                      )}
                                    </div>
                                    {menuSuggestions.map((menuItem) => (
                                      <div
                                        key={menuItem.id || menuItem.menu_item_id || menuItem.name}
                                        className="suggestion-item"
                                        onClick={() => selectMenuSuggestion(menuItem)}
                                        style={{
                                          padding: '10px 12px',
                                          cursor: 'pointer',
                                          borderBottom: '1px solid #f3f4f6',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          transition: 'background 0.2s',
                                          background: 'white'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                      >
                                        <span style={{ fontWeight: 500 }}>{toTitleCase(menuItem.name)}</span>
                                        <span style={{
                                          background: '#f3f4f6',
                                          padding: '2px 8px',
                                          borderRadius: 4,
                                          fontSize: 11,
                                          color: '#666',
                                          fontWeight: 600
                                        }}>IMPORT</span>
                                      </div>
                                    ))}
                                  </>
                                )}

                                {/* Recipe Library Section */}
                                {librarySuggestions.length > 0 && (
                                  <>
                                    <div style={{
                                      padding: '8px 12px',
                                      background: '#f9fafb',
                                      borderBottom: '1px solid #e5e7eb',
                                      fontWeight: 600,
                                      fontSize: 12,
                                      color: '#666',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      marginTop: (suggestions.length > 0 || preparationSuggestions.length > 0 || menuSuggestions.length > 0) ? 4 : 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}>
                                      <span>Recipe Library</span>
                                      {suggestions.length === 0 && preparationSuggestions.length === 0 && menuSuggestions.length === 0 && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            cancelEditing();
                                          }}
                                          title="Close suggestions"
                                          style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: 999,
                                            border: '1px solid #d1d5db',
                                            background: '#fff',
                                            color: '#6b7280',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            padding: 0
                                          }}
                                        >
                                          <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                                        </button>
                                      )}
                                    </div>
                                    {librarySuggestions.map((r) => (
                                      <div
                                        key={r.recipe_library_id || r.item_name || r.name}
                                        className="suggestion-item"
                                        onClick={() => selectLibrarySuggestion(r)}
                                        style={{
                                          padding: '10px 12px',
                                          cursor: 'pointer',
                                          borderBottom: '1px solid #f3f4f6',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          transition: 'background 0.2s',
                                          background: 'white'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                      >
                                        <span style={{ fontWeight: 500 }}>{toTitleCase(r.item_name || r.name)}</span>
                                        <span style={{
                                          background: '#f3f4f6',
                                          padding: '2px 8px',
                                          borderRadius: 4,
                                          fontSize: 11,
                                          color: '#666',
                                          fontWeight: 600
                                        }}>IMPORT</span>
                                      </div>
                                    ))}
                                  </>
                                )}

                                {suggestions.length === 0 && preparationSuggestions.length === 0 && menuSuggestions.length === 0 && librarySuggestions.length === 0 && (
                                  <div style={{
                                    padding: '16px',
                                    textAlign: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '12px'
                                  }}>
                                    {formData.name?.trim() ? (
                                      <>
                                        <span style={{ color: '#666', fontSize: 14 }}>
                                          Click Add to save "{formData.name}"
                                        </span>
                                        <button
                                          onClick={() => {
                                            setShowSuggestions(false);
                                            if (quantityInputRef.current) {
                                              quantityInputRef.current.focus();
                                              quantityInputRef.current.select();
                                            }
                                          }}
                                          style={{
                                            background: '#000',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '10px 24px',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            transition: 'background 0.2s'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.background = '#333'}
                                          onMouseLeave={(e) => e.currentTarget.style.background = '#000'}
                                        >
                                          Add
                                        </button>
                                      </>
                                    ) : (
                                      <span style={{ color: '#999', fontSize: 14 }}>
                                        Start typing to search ingredients or menu items
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        <div className="itemmap-ingredient-quantity-row" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <div className="controls-inline">
                            <button
                              className="square-btn"
                              onClick={() => setFormData({ ...formData, quantity: Math.max(0, Number(formData.quantity) - 1) })}
                            >
                              −
                            </button>
                            <input
                              ref={quantityInputRef}
                              type="number"
                              className="number-input"
                              value={formData.quantity}
                              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                              onWheel={(e) => e.target.blur()}
                            />
                            <button
                              className="square-btn"
                              onClick={() => setFormData({ ...formData, quantity: Number(formData.quantity) + 1 })}
                            >
                              ＋
                            </button>
                            <SearchableSelect
                              key={`comps-unit-edit-${idx}`}
                              value={formData.unit}
                              onChange={(newUnit) => setFormData({ ...formData, unit: newUnit })}
                              options={unitOptions}
                              style={{ width: 82 }}
                              openUpward={idx === ingredients.length - 1}
                            />
                            <button
                              className="ingredient-delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteIngredient(idx);
                              }}
                              title="Delete"
                              type="button"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                        <div
                          className="ingredient-name clickable"
                          style={{ fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, cursor: 'pointer', color: '#000000' }}
                          onClick={() => startEditing(idx)}
                        >
                          {ingredient.name ? ingredient.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : 'New Ingredient'}
                        </div>
                        {savedPreparationByName.has(normalizeSummaryKey(ingredient.name)) && (
                          <span
                            className="itemmap-prep-dot"
                            title="Saved preparation"
                            aria-label="Saved preparation"
                            style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#FBBF24', flexShrink: 0 }}
                          />
                        )}
                      </div>
                      {ingredient.name !== 'New Ingredient' && (
                        <div className="controls-inline">
                          <button
                            className="square-btn"
                            onClick={() => updateQuantity(idx, -1)}
                          >
                            −
                          </button>
                          <input
                            type="number"
                            className="number-input"
                            value={ingredient.quantity}
                            onChange={(e) => handleQuantityChange(idx, e.target.value)}
                            placeholder="0"
                            onWheel={(e) => e.target.blur()}
                          />
                          <button
                            className="square-btn"
                            onClick={() => updateQuantity(idx, 1)}
                          >
                            ＋
                          </button>
                          <SearchableSelect
                            key={`comps-unit-display-${idx}`}
                            value={ingredient.unit || 'g'}
                            onChange={(newUnit) => handleUnitChange(idx, newUnit)}
                            options={unitOptions}
                            style={{ width: 82 }}
                            openUpward={idx === ingredients.length - 1}
                            isOpen={openUnitIndex === idx && editingIndex !== idx}
                            onToggle={(isOpen) => setOpenUnitIndex(prev => isOpen ? idx : (prev === idx ? null : prev))}
                          />
                          <button
                            className="ingredient-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteIngredient(idx);
                            }}
                            title="Delete"
                            type="button"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
          </div>

          {/* Add Ingredient Button - matching itemmap style */}
          <div className="comps-add-ingredient-row">
            <button className="comps-add-ingredient-button" onClick={addNewIngredient}>
              Add Ingredient
            </button>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="comps-footer-actions">
          <div className="comps-footer-row">
            <button
              className="comps-footer-btn comps-footer-btn--primary"
              onClick={handleSave}
              disabled={isConfirmingSave}
            >
              {isConfirmingSave ? 'Confirming...' : 'Confirm'}
            </button>
          </div>
        </div>

        {showSaveSuccessModal && (
          <div className="comps-modal-overlay">
            <div className="comps-success-modal-card">
              <div className="comps-success-modal-icon-wrap">
                <div className="comps-success-modal-icon-circle">
                  <FontAwesomeIcon icon={faCheck} className="comps-success-modal-icon" />
                </div>
              </div>
              <div className="comps-success-modal-message">
                Complimentary Item Saved Successfully
              </div>
              <button
                type="button"
                className="comps-success-modal-ok-btn"
                onClick={handleSaveSuccessOk}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* Delete All Modal */}
        {showImportConfirmModal && selectedMenuItemForImport && (
          <div className="comps-modal-overlay">
            <div className="comps-import-modal-card">
              <div className="comps-import-modal-copy-block">
                <h3 className="comps-import-modal-title">Import Ingredients?</h3>
                <p className="comps-import-modal-copy">
                  Do you want to import all ingredients from <strong>"{toTitleCase(selectedMenuItemForImport.name)}"</strong>?
                </p>
              </div>
              <div className="comps-modal-actions">
                <button
                  onClick={() => {
                    setShowImportConfirmModal(false);
                    setSelectedMenuItemForImport(null);
                  }}
                  className="comps-modal-btn-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmImportIngredients}
                  className="comps-import-modal-btn-confirm"
                >
                  Import
                </button>
              </div>
            </div>
          </div>
        )}

        {showUnsavedChangesModal && (
          <div className="comps-modal-overlay">
            <div className="comps-import-modal-card">
              <div className="comps-import-modal-copy-block">
                <h3 className="comps-import-modal-title">Leave Without Saving?</h3>
                <p className="comps-import-modal-copy">
                  You have unsaved changes. If you leave now, all ingredient edits on <strong>&quot;{itemDisplayName}&quot;</strong> will be lost.
                </p>
              </div>
              <div className="comps-modal-actions">
                <button
                  onClick={() => setShowUnsavedChangesModal(false)}
                  className="comps-modal-btn-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={discardUnsavedChangesAndGoBack}
                  className="comps-modal-btn-confirm"
                >
                  Leave
                </button>
              </div>
            </div>
          </div>
        )}

        {validationError && (
          <div className="comps-modal-overlay">
            <div className="comps-modal-card" style={{ textAlign: 'center' }}>
              <h3 style={{ marginBottom: 16, color: '#000' }}>Alert</h3>
              <p style={{ marginBottom: 24, color: '#666' }}>{validationError}</p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => setValidationError(null)}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    background: '#000000',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteAllModal && (
          <div className="comps-modal-overlay">
            <div className="comps-modal-card">
              <h3 style={{ marginBottom: 16, color: '#000' }}>Delete All Ingredients?</h3>
              <p style={{ marginBottom: 24, color: '#666' }}>
                This will remove all ingredients from this item. You can undo this action.
              </p>
              <div className="comps-modal-actions">
                <button
                  onClick={() => setShowDeleteAllModal(false)}
                  className="comps-modal-btn-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAll}
                  className="comps-modal-btn-delete"
                >
                  Delete All
                </button>
              </div>
            </div>
          </div>
        )}

        {showPreparationCreator && (
          <PreparationCreatorModal
            businessId={UnitMappingService.getBusinessId()}
            onSave={(result) => {
              console.log('🆕 Preparation created:', result);
              loadSavedPreparations();
              setShowPreparationCreator(false);
            }}
            onCancel={() => {
              console.log('❌ Preparation creation cancelled');
              setShowPreparationCreator(false);
            }}
          />
        )}
      </div>
      {/* End of content wrapper */}
    </div>
  );
};

export default ComplimentaryItemsPage;
